import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid
} from 'recharts';

export default function TelemetryCharts({ telemetryData }) {
    if (telemetryData == null
        || telemetryData.totalEntitiesProcessed == null
        || telemetryData.totalEntitiesProcessed === 0) {
        return <p>No telemetry data available</p>;
    }

    const toSeconds = (ms) => Number((ms / 1000).toFixed(2));

    // Chart 1: Aggregates per service
    const serviceChartData = telemetryData.aggregatePerService.map((service) => ({
        serviceName: service.service,
        avgProcessingTime: toSeconds(
            service.aggregatePerEntityType.reduce(
                (sum, e) => sum + e.averageProcessingTimeInMilliseconds,
                0
            ) / service.aggregatePerEntityType.length
        ),
        totalCount: service.totalEntitiesProcessed
    }));

    // Chart 2: Aggregates per entity type
    const entityChartData = [];
    const allServices = telemetryData.aggregatePerService.map((s) => s.service);

    telemetryData.aggregatePerService.forEach((service) => {
        service.aggregatePerEntityType.forEach((entity) => {
            let entityEntry = entityChartData.find((e) => e.entityType === entity.entityType);
            if (!entityEntry) {
                entityEntry = { entityType: entity.entityType };
                entityChartData.push(entityEntry);
            }
            entityEntry[service.service] = toSeconds(entity.averageProcessingTimeInMilliseconds);
        });
    });

    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '2rem',
                marginBottom: '50px'
            }}
        >
            {/* Chart 1 */}
            <div style={{
                flex: '1 1 700px',
                minWidth: '700px',
                maxWidth: '700px',
                height: '400px',
                marginBottom: '25px'
            }}
            >
                <h3>Avg Processing Time per Service</h3>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={serviceChartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 0,
                            bottom: 5
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="serviceName" />
                        <YAxis tickFormatter={(v) => `${v}s`} />
                        <Tooltip formatter={(v) => `${v}s`} />
                        <Legend />
                        <Bar dataKey="avgProcessingTime" fill="#F75258" name="Avg Time" />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Chart 2 */}
            <div style={{
                flex: '1 1 700px',
                minWidth: '700px',
                maxWidth: '700px',
                height: '400px',
                marginBottom: '25px'
            }}
            >
                <h3>Avg Processing Time per Entity Type (per Service)</h3>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={entityChartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 0,
                            bottom: 5
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="entityType"
                            tick={(props) => {
                                const { x, y, payload } = props;
                                const fontSize = entityChartData.length > 4 ? 12 : 16;
                                return (
                                    <text x={x} y={y + fontSize - 2} textAnchor="middle" fontSize={fontSize} fill="#666">
                                        {payload.value}
                                    </text>
                                );
                            }}
                            interval={0}
                        />
                        <YAxis tickFormatter={(v) => `${v}s`} />
                        <Tooltip formatter={(v) => `${v}s`} />
                        <Legend />
                        {allServices.map((service, index) => (
                            <Bar
                                key={service}
                                dataKey={service}
                                fill={['#F75258', '#82ca9d', '#8884d8', '#ffc658'][index % 4]}
                                name={service}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Chart 3 */}
            <div style={{
                flex: '1 1 700px',
                minWidth: '700px',
                maxWidth: '700px',
                height: '400px',
                marginBottom: '25px'
            }}
            >
                <h3>Total Entities Processed per Service</h3>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={serviceChartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 0,
                            bottom: 5
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="serviceName" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="totalCount" fill="#F75258" name="Entities Processed" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
